import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { IMG_PALM_BEACH } from "@/lib/assets";
import { WORKSHOPS } from "@/data/workshops";
import { Clock, Users, Calendar, ArrowRight, Loader2 } from "lucide-react";
import Booking from "./Booking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Local demo workshops, mapped to the API shape — used as a fallback so the page is never empty.
const FALLBACK_WORKSHOPS = WORKSHOPS.map((w, i) => ({
  id: w.id,
  order: i,
  day_fr: w.day.fr,
  day_en: w.day.en,
  title_fr: w.title.fr,
  title_en: w.title.en,
  age: w.age,
  duration_fr: w.duration.fr,
  duration_en: w.duration.en,
  desc_fr: w.desc.fr,
  desc_en: w.desc.en,
  img: w.img,
  spots: w.spots,
  available: w.available,
}));

export default function Workshops() {
  const { t, lang } = useLang();
  const w = t.workshops;
  const [items, setItems] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/workshops`)
      .then((r) => {
        const list = r.data?.workshops || [];
        setItems(list.length ? list : FALLBACK_WORKSHOPS);
      })
      .catch(() => setItems(FALLBACK_WORKSHOPS));
  }, []);

  const pick = (ws, key) => ws[`${key}_${lang}`] || ws[`${key}_fr`] || "";

  return (
    <div data-testid="workshops-page" className="bg-[#fbf6ec] min-h-screen">
      <div className="relative pt-32 pb-16 bg-[#6f7d54] overflow-hidden">
        <img src={IMG_PALM_BEACH} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#6f7d54] to-[#6f7d54]/50" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 lg:px-10">
          <span className="font-script text-[#e0b36a] text-3xl">{w.hero.kicker}</span>
          <h1 className="font-head font-extrabold text-white text-4xl md:text-6xl tracking-tight mt-1 mb-4">{w.hero.title}</h1>
          <p className="font-body text-[#f7efe0]/85 text-lg max-w-2xl">{w.hero.subtitle}</p>
        </div>
      </div>

      <Booking mode="atelier" embedded />

      <div className="max-w-6xl mx-auto px-5 lg:px-10 pt-12">
        <h2 className="font-head font-extrabold text-[#6f7d54] text-2xl md:text-3xl tracking-tight">{w.specialTitle}</h2>
        <p className="font-body text-[#3a4d68] mt-2 max-w-2xl">{w.specialSubtitle}</p>
      </div>

      {items === null ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#e07a5f]" size={36} /></div>
      ) : (
        <div className="max-w-6xl mx-auto px-5 lg:px-10 pt-6 pb-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((ws, i) => (
            <div key={ws.id} data-testid={`workshop-${i}`} className="group bg-white rounded-3xl overflow-hidden border border-[#6f7d54]/10 hover:-translate-y-1.5 hover:shadow-2xl transition-all duration-300 flex flex-col">
              <div className="relative h-48 overflow-hidden">
                {ws.img ? <img src={ws.img} alt={pick(ws, "title")} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-[#e0b36a]/20" />}
                <span className="absolute top-3 left-3 rounded-full bg-[#2e8b9e] text-white text-xs font-bold px-3 py-1 flex items-center gap-1"><Calendar size={12} /> {pick(ws, "day")}</span>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-head font-bold text-[#6f7d54] text-lg mb-2">{pick(ws, "title")}</h3>
                <p className="font-body text-[#3a4d68]/90 text-[15px] leading-relaxed mb-4 flex-1">{pick(ws, "desc")}</p>
                <div className="flex items-center gap-4 text-sm text-[#3a4d68] font-semibold mb-5">
                  <span className="flex items-center gap-1.5"><Users size={15} className="text-[#e07a5f]" /> {w.age} {ws.age}</span>
                  <span className="flex items-center gap-1.5"><Clock size={15} className="text-[#e07a5f]" /> {pick(ws, "duration")}</span>
                </div>
                {ws.available ? (
                  <Link to="/reservation" data-testid={`workshop-book-${i}`} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] text-white font-bold px-5 py-3 transition-colors">
                    {w.book} <ArrowRight size={18} />
                  </Link>
                ) : (
                  <span className="inline-flex items-center justify-center rounded-full bg-[#6f7d54]/8 text-[#3a4d68] font-bold px-5 py-3">{w.soon}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
